import type { ChatMode } from '@/data-sources/contract';

import { delay } from '../scenarios';

/**
 * The theatre of an answer arriving — the prototype's `state/streaming.ts`,
 * constants and unit splitting unchanged.
 *
 * The answer itself is computed the moment the question is asked — the engine
 * is deterministic and sub-millisecond — so "thinking" and "streaming" are
 * pacing, not work. In the prototype the provider's timers and the view's
 * reveal shared these numbers; here the mock stream emits on the same cadence,
 * so the client simply shows each unit as it arrives.
 */

/**
 * How long the agent thinks before the first word. The same 6000ms for every
 * query and both modes (the user's call, 2026-09-20; it was 3600) — the modes
 * differ in the depth of the answer, not in the wait. Kept per mode so that can
 * change again without touching a caller.
 */
export const THINK_MS: Readonly<Record<ChatMode, number>> = { fast: 6000, expert: 6000 };

/** Milliseconds per revealed unit — about 22 words a second. */
export const STREAM_MS_PER_UNIT = 45;

/** A beat after the last word before the sources, chart and follow-ups arrive. */
export const SETTLE_MS = 200;

/**
 * Split an answer into the units the stream reveals one at a time.
 *
 * A word is a unit, and so is a whole `**bold run**` or `[n]` marker: revealing
 * those a word at a time would flash a lone `**` or `[` on screen, since
 * `StreamingText` only recognises the complete form. Whitespace stays attached
 * to the unit before it, so joining any prefix of the units reproduces the
 * original text exactly.
 */
export function splitUnits(text: string): string[] {
  const tokens = text.split(/(\*\*[^*]+\*\*|\[\d+\]|\s+)/).filter(Boolean);
  const units: string[] = [];
  for (const token of tokens) {
    if (/^\s+$/.test(token) && units.length > 0) units[units.length - 1] += token;
    else units.push(token);
  }
  return units;
}

/** How long a text takes to stream in full. */
export function streamDuration(text: string): number {
  return splitUnits(text).length * STREAM_MS_PER_UNIT + SETTLE_MS;
}

/**
 * A clock that waits until an offset from its start, so a long answer does not
 * drift the way chained `setTimeout(45)` calls would: the prototype revealed
 * `floor(elapsed / 45)` units, i.e. unit k at exactly `k * 45` ms. A pacing
 * clock (`performance.now`), not a date — nothing it measures reaches the data.
 */
export function pacer(signal?: AbortSignal) {
  const start = performance.now();
  return async (offsetMs: number): Promise<void> => {
    signal?.throwIfAborted();
    await delay(start + offsetMs - performance.now(), signal);
    signal?.throwIfAborted();
  };
}
